import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { MfaPromptPage } from "@/pages/MfaPromptPage";
import { MfaEnrollPage } from "@/pages/MfaEnrollPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { AdminUsersPage } from "@/pages/admin/AdminUsersPage";
import { AdminInvitesPage } from "@/pages/admin/AdminInvitesPage";
import { AdminQueuesPage } from "@/pages/admin/AdminQueuesPage";
import { OnboardingWizard } from "@/pages/onboarding/OnboardingWizard";
import { EmailAccountsPage } from "@/pages/settings/EmailAccountsPage";
import { EmailsPage } from "@/pages/EmailsPage";
import { BankStatementsPage } from "@/pages/BankStatementsPage";
import { CaseloadPage } from "@/pages/counselor/CaseloadPage";
import { ClientDetailPage } from "@/pages/counselor/ClientDetailPage";
import { ReviewQueuePage } from "@/pages/counselor/ReviewQueuePage";
import { MunicipalityDashboardPage } from "@/pages/municipality/MunicipalityDashboardPage";
import { CounselorsPage as MunicipalityCounselorsPage } from "@/pages/municipality/CounselorsPage";
import { MunicipalityAuditPage } from "@/pages/municipality/MunicipalityAuditPage";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import {
  MutationErrorBanner,
  meldMutatieFout,
  type MutatieMeta,
} from "@/components/MutationErrorBanner";

const queryClient = new QueryClient({
  /**
   * Foutafhandeling voor élke mutatie, op één plek. Dit hangt bewust aan de
   * MutationCache en niet aan defaultOptions.mutations: een `onError` op een
   * losse mutatie vervangt de default, maar zet de cache-callback niet opzij.
   * Zo kan geen enkele mutatie stil falen, ook niet eentje die hierna nog
   * geschreven wordt.
   *
   * Aanleiding: vier kernpaden faalden maandenlang zonder dat er iets in beeld
   * kwam. Zie BETA-VOORTGANG.md.
   */
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) =>
      meldMutatieFout(error, mutation.options.meta as MutatieMeta | undefined),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MutationErrorBanner />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/mfa" element={<MfaPromptPage />} />
          <Route
            path="/settings/mfa"
            element={
              <ProtectedRoute>
                <MfaEnrollPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/email"
            element={
              <ProtectedRoute>
                <EmailAccountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emails"
            element={
              <ProtectedRoute>
                <EmailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bank-statements"
            element={
              <ProtectedRoute>
                <BankStatementsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/invites"
            element={
              <ProtectedRoute>
                <AdminInvitesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/queues"
            element={
              <ProtectedRoute>
                <AdminQueuesPage />
              </ProtectedRoute>
            }
          />
          <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
          <Route
            path="/counselor"
            element={
              <ProtectedRoute>
                <CaseloadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/counselor/clients/:id"
            element={
              <ProtectedRoute>
                <ClientDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/counselor/review"
            element={
              <ProtectedRoute>
                <ReviewQueuePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/municipality"
            element={
              <ProtectedRoute>
                <MunicipalityDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/municipality/counselors"
            element={
              <ProtectedRoute>
                <MunicipalityCounselorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/municipality/audit"
            element={
              <ProtectedRoute>
                <MunicipalityAuditPage />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
